import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime } from 'luxon';
import {
  Box,
  Dialog,
  DialogContent,
  LinearProgress,
  styled,
} from '@mui/material';

import { usePortalContext } from '@/contexts/PortalProvider';

import { useAppDispatch } from '@/hooks/useAppDispatch';

import { AppDispatch } from '@/data/store';
import { ReservationFormSteps } from './schema/reservationFormSteps.model';
import { Passenger } from '@/data/models/passenger.model';
import type { Reservation } from '@/data/models/reservation.model';
import { ServicePlan } from '@/data/models/service-plan.model';
import type { TransportationAttribute } from '@/data/models/transportation-attribute.model';
import { setJustCreatedReservation } from '@/data/reservation.slice';
import {
  ReservationFormInput,
  MEMBER_FORM_PHONE_FIELDS,
  reservationFormSchema,
  buildEmptyReservationFormValues,
  type ReservationFormValues,
} from './schema/reservationForm.schema';

import reservationUtils from '@/services/reservationUtils';
import { triggerReservationPhoneFieldValidation } from './utils/triggerReservationPhoneFieldValidation';
import { useTripLegValidationFormApplier } from './hooks/useTripLegValidationFormApplier';

import * as lookupSlice from '@/data/lookup.slice';

import {
  ReservationFormMachinesProvider,
  useReservationFormMachines,
} from './ReservationFormMachinesContext';
import { ReservationFormDebug } from './hooks/useDebugReservationMachines';

import ReservationDialogHeader from '@/components/home/reservations/ReservationDialogHeader';
import { ReservationTripLegForm } from './ReservationTripLegForm';
import { ReservationMemberForm } from './ReservationMemberForm';
import { ReservationSummary } from './ReservationSummary';
import RequestOverrideDialog from './prompts/RequestOverrideDialog';
import ConfirmationDialog from '../ConfirmationDialog';

const PREFIX = 'CreateReservationDialog';

const classes = {
  content: `${PREFIX}-content`,
  pages: `${PREFIX}-pages`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    padding: 0,
    overflow: 'visible',
  },
  [`& .${classes.pages}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    maxWidth: '1152px',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(18),
      paddingRight: theme.spacing(18),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
}));

function ReservationFormSubmit({
  form,
  onSubmit,
}: {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  onSubmit: (
    values: ReservationFormValues,
    options: {
      saveAsDraft: boolean;
      createdReservation: Reservation | null;
    },
  ) => Promise<void>;
}) {
  const { mainSnapshot } = useReservationFormMachines();

  const wasSuccessRef = useRef(false);
  useEffect(() => {
    if (!mainSnapshot.matches('success')) {
      wasSuccessRef.current = false;
      return;
    }
    if (wasSuccessRef.current) return;
    wasSuccessRef.current = true;
    if (onSubmit) {
      onSubmit(form.getValues() as ReservationFormValues, {
        saveAsDraft: Boolean(mainSnapshot.context.saveAsDraft),
        createdReservation: mainSnapshot.context.createdReservation ?? null,
      });
    }
  }, [mainSnapshot, onSubmit, form]);

  return null;
}

function ReservationFormSubmitProgress() {
  const { mainSnapshot } = useReservationFormMachines();
  const isSubmitting =
    mainSnapshot.matches('submitting') || mainSnapshot.matches('validating');

  if (!isSubmitting) return null;

  return <LinearProgress />;
}

interface CreateReservationDialogProps {
  open: boolean;
  onClose: () => void;
  passenger: Passenger | null;
  servicePlans: ServicePlan[];
  transportationAttributes?: TransportationAttribute[];
}

const CreateReservationDialog = ({
  open,
  onClose,
  passenger,
  servicePlans,
  transportationAttributes,
}: CreateReservationDialogProps) => {
  const { t } = useTranslation('common');
  const dispatch: AppDispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const activeStepRef = useRef(activeStep);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { isMemberPortal } = usePortalContext();

  // Check the runtimeConfig setting for state machine debugging
  const { __environment__ } = window as any;
  const stateMachineDebugEnabled = Boolean(
    __environment__?.REACT_APP_STATE_MACHINE_DEBUG,
  );

  const form = useForm<ReservationFormInput, unknown, ReservationFormValues>({
    resolver: zodResolver(reservationFormSchema),
    defaultValues: buildEmptyReservationFormValues(
      passenger,
      transportationAttributes,
    ),
    mode: 'onBlur',
  });
  const { isDirty } = form.formState;
  const pickUpTime = form.watch('trips.0.pickupDateTime');
  const appointmentDate = form.watch('trips.0.appointmentDate');

  useLayoutEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  useEffect(() => {
    if (!open || activeStep !== ReservationFormSteps.MEMBER) return;
    triggerReservationPhoneFieldValidation(form);
  }, [open, activeStep, form]);

  useEffect(() => {
    if (!open) return;
    form.reset(
      buildEmptyReservationFormValues(passenger, transportationAttributes),
    );
    triggerReservationPhoneFieldValidation(form);
  }, [open, passenger?.passengerUUID, transportationAttributes]);

  // Member-step-aware: member phone fields are excluded from the post-apply
  // phone revalidation unless the member step is the one being viewed.
  const applyTripLegValidationRef = useTripLegValidationFormApplier(
    form,
    () =>
      activeStepRef.current === ReservationFormSteps.MEMBER
        ? undefined
        : { fieldsToExclude: MEMBER_FORM_PHONE_FIELDS },
    passenger?.clientBC,
    passenger?.servicePlans?.[0]?.servicePlanUUID,
  );

  const tripLegServiceDate =
    (pickUpTime instanceof DateTime && pickUpTime.isValid
      ? pickUpTime.toISODate()
      : null) ??
    (appointmentDate instanceof DateTime && appointmentDate.isValid
      ? appointmentDate.toISODate()
      : null) ??
    (typeof appointmentDate === 'string' && appointmentDate.trim()
      ? appointmentDate
      : null) ??
    undefined;
  const overridableServicePlans = servicePlans.filter(
    ({ effectiveStartDate, effectiveEndDate }) =>
      tripLegServiceDate != null &&
      effectiveStartDate <= tripLegServiceDate &&
      (!effectiveEndDate || tripLegServiceDate <= effectiveEndDate),
  );

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleReservationSubmit = useCallback(
    async (
      _values: ReservationFormValues,
      options: {
        saveAsDraft: boolean;
        createdReservation: Reservation | null;
      },
    ) => {
      const createdReservationReferenceID =
        options.createdReservation?.reservationReferenceID;
      if (createdReservationReferenceID && passenger?.passengerUUID) {
        dispatch(
          setJustCreatedReservation({
            passengerUUID: passenger.passengerUUID,
            reservationReferenceID: createdReservationReferenceID,
          }),
        );
      }
      form.reset(
        buildEmptyReservationFormValues(passenger, transportationAttributes),
      );
      setActiveStep(0);
      onClose();
    },
    [form, onClose, passenger, transportationAttributes],
  );

  const handlePrevious = (evt: any) => {
    evt.preventDefault();
    setActiveStep(activeStep - 1);
  };

  const handleNext = (evt: any) => {
    evt.preventDefault();
    setActiveStep(activeStep + 1);
  };

  const handleCloseDialog = () => {
    if (isDirty) {
      setConfirmDialogOpen(true);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    // Fetch all Create Reservation dialog data that isn't passed via props
    dispatch(lookupSlice.fetchTransportationAttributeMapAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegPassengerNoteType'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegDriverNoteType'));
  }, []);

  return (
    <StyledDialog open={open} onClose={onClose} fullScreen>
      <ReservationDialogHeader
        passengerName={
          isMemberPortal
            ? undefined
            : `${passenger?.firstName} ${passenger?.lastName}`
        }
        title={
          isMemberPortal ? t('member.bookATrip') : t('reservation.createTrip')
        }
        steps={[
          t('reservation.tripDetailsStep'),
          t('reservation.member'),
          t('reservation.summary'),
        ]}
        activeStep={activeStep}
        isValid={form.formState.isValid}
        step={handleStep}
        open={handleCloseDialog}
      />
      <ReservationFormMachinesProvider
        clientBC={passenger?.clientBC ?? ''}
        passengerUUID={passenger?.passengerUUID ?? ''}
        passengerPhone={
          passenger ? reservationUtils.getPassengerPhone(passenger) : undefined
        }
        applyTripLegValidationResultsToForm={() =>
          applyTripLegValidationRef.current
        }
        getPickupTimeZoneForTrip={(tripIndex) =>
          form.getValues('trips')[tripIndex]?.pickupTimeZone || undefined
        }>
        <ReservationFormSubmitProgress />
        <DialogContent className={classes.content}>
          <Box className={classes.pages}>
            {stateMachineDebugEnabled && <ReservationFormDebug form={form} />}
            <ReservationFormSubmit
              form={form}
              onSubmit={handleReservationSubmit}
            />
            <RequestOverrideDialog
              servicePlans={overridableServicePlans}
              reservationForm={form}
            />
            {activeStep === 0 && (
              <ReservationTripLegForm
                form={form}
                isAdding={false}
                isEditing={false}
                onNext={handleNext}
              />
            )}
            {activeStep === 1 && (
              <ReservationMemberForm
                form={form}
                transportationAttributes={transportationAttributes ?? []}
                onNext={handleNext}
                onPrevious={handlePrevious}
              />
            )}
            {activeStep === 2 && (
              <ReservationSummary form={form} onPrevious={handlePrevious} />
            )}
          </Box>
        </DialogContent>
      </ReservationFormMachinesProvider>
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmDialogOpen}
        positiveAction={onClose}
        negativeAction={() => setConfirmDialogOpen(false)}
      />
    </StyledDialog>
  );
};

export { CreateReservationDialog };
